import React from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {
  X,
  Settings,
  Calendar,
  LogOut,
  Star,
  User as UserIcon,
  Search as SearchIcon,
  CreditCard,
  Clock,
  Wallet,
  DollarSign,
} from 'lucide-react';
import {cn} from '../util/designUtil';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({isOpen, onClose}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {user, logout} = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    {label: 'Find Influencers', icon: SearchIcon, path: '/'},
    {label: 'My Bookings', icon: Calendar, path: '/my-bookings'},
    {label: 'Buy Credits', icon: CreditCard, path: '/buy-credits'},
    ...(user?.isStarAccount ? [{label: 'Payouts & Earnings', icon: DollarSign, path: '/payouts'}] : []),
    {label: 'Settings', icon: Settings, path: '/settings'},
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed top-0 left-0 h-full bg-white text-gray-700 z-50 transform transition-transform duration-300 ease-in-out flex flex-col shadow-lg',
          'w-72 border-r border-gray-200',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}>
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 min-h-[60px]">
          {/* Left Part: User Info & Credits or Menu Title */}
          {user ? (
            <div className="flex flex-col gap-1.5"> {/* Main container for user info + credits */}
              {/* User Avatar & Name Row */}
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-5 h-5 text-gray-500" />
                </div>
                <span className="text-sm font-medium text-gray-700 truncate">
                  {user.name || user.displayName || user.email}
                </span>
              </div>
              {/* Credits Row - Placed below name/email */}
              {user.credits !== undefined && (
                <div 
                  className="flex items-center gap-1.5 text-sm bg-gray-100 px-2.5 py-1.5 rounded-full self-start" 
                  title={`${user.credits} Credits`}
                >
                  <Wallet className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="font-medium text-gray-700">{user.credits?.toLocaleString()}</span>
                </div>
              )}
            </div>
          ) : (
            <h2 className="text-lg font-semibold text-purple-600">Menu</h2>
          )}

          {/* Right Part: Close Button */}
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-purple-600 p-1.5 rounded-md hover:bg-purple-50 transition-colors"
            aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content / Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  onClose();
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500',
                  isActive
                    ? 'bg-purple-100 text-purple-700 font-medium'
                    : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600',
                )}>
                <item.icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0',
                    isActive ? 'text-purple-700' : 'text-gray-500',
                  )}
                />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Become a Star Button Section */}
        {user && user.isStarAccount && (
          <div className="px-4 pb-2 pt-2">
            <button
              onClick={() => {
                navigate('/manage-availability');
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md transform transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white">
              <Calendar className="h-5 w-5" />
              Manage Availability
            </button>
          </div>
        )}

        {/* Become a Star Button Section */}
        {user && (
          <div className="p-4 pt-2">
            <button
              onClick={() => {
                navigate('/become-star');
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md transform transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white">
              <Star className="h-5 w-5" />
              {user.isStarAccount ? 'Connect Accounts' : 'Become a Star'}
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          {user && (
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-100 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 transition-colors">
              <LogOut className="h-5 w-5 flex-shrink-0 text-gray-500" />
              <span>Log Out</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
